import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Table, Button, Space, Modal, Input, InputNumber,
  message, Tag, Segmented,
} from "antd";
import {
  ReloadOutlined, PlusCircleOutlined, MinusCircleOutlined,
  WalletOutlined, DeleteOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { apiClient } from "../../../shared/api/AllApi";
import { formatCurrency } from "../../../utils/formatters";
import { readCachedSystemConfig } from "../../../shared/lib/systemConfig";
import usePermissions from "../../../hooks/usePermissions";
import { PageHeader, StatCard, T, cardStyle } from "../../../shared/ui/PageShell";
import DateRangeFilter from "../../../shared/ui/DateRangeFilter";
import useTablePagination from "../../../shared/ui/useTablePagination";
import { fixedStart } from "../../../i18";

const CashManagement = () => {
  const { t, i18n } = useTranslation();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission("cash.manage");
  const mainCode = readCachedSystemConfig()?.currency_code || "IQD";
  const { pagination } = useTablePagination();

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ count: 0, total_deposits: 0, total_withdrawals: 0, net: 0 });
  const [cashOnHand, setCashOnHand] = useState(0);
  const [range, setRange] = useState([null, null]);
  const [typeFilter, setTypeFilter] = useState("all");

  const [formOpen, setFormOpen] = useState(false);
  const [formType, setFormType] = useState("deposit");
  const [amount, setAmount] = useState(null);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (range?.[0]) params.from = dayjs(range[0]).format("YYYY-MM-DD");
      if (range?.[1]) params.to = dayjs(range[1]).format("YYYY-MM-DD");
      if (typeFilter !== "all") params.type = typeFilter;
      const res = await apiClient.get("/cash-movements", { params });
      setRows(Array.isArray(res.data?.rows) ? res.data.rows : []);
      setStats(res.data?.stats || { count: 0, total_deposits: 0, total_withdrawals: 0, net: 0 });
      setCashOnHand(Number(res.data?.cash_on_hand) || 0);
    } catch (e) {
      message.error(e?.response?.data?.message || t("cashLoadFailed", "Failed to load cash movements"));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [range, typeFilter, t]);

  useEffect(() => { load(); }, [load]);

  const openForm = (type) => {
    setFormType(type);
    setAmount(null);
    setReason("");
    setFormOpen(true);
  };

  const submitForm = async () => {
    const amt = Number(amount) || 0;
    if (amt <= 0) {
      message.error(t("cashEnterAmount", "Enter an amount greater than zero."));
      return;
    }
    setSaving(true);
    try {
      await apiClient.post("/cash-movements", {
        type: formType,
        amount: amt,
        reason: reason.trim() || undefined,
      });
      message.success(
        formType === "deposit"
          ? t("cashDepositSuccess", "Deposit recorded.")
          : t("cashWithdrawalSuccess", "Withdrawal recorded.")
      );
      setFormOpen(false);
      await load();
    } catch (e) {
      message.error(e?.response?.data?.message || t("cashSaveFailed", "Failed to record."));
    } finally {
      setSaving(false);
    }
  };

  const removeMovement = (row) => {
    Modal.confirm({
      title: t("cashDeleteTitle", "Delete this cash movement?"),
      content: t("cashDeleteHint", "This will remove it from the cash total."),
      okText: t("confirm", "Confirm"),
      okButtonProps: { danger: true },
      cancelText: t("cancel", "Cancel"),
      onOk: async () => {
        try {
          await apiClient.delete(`/cash-movements/${row.id}`);
          message.success(t("cashDeleted", "Cash movement deleted."));
          await load();
        } catch (e) {
          message.error(e?.response?.data?.message || t("cashDeleteFailed", "Failed to delete."));
        }
      },
    });
  };

  const columns = useMemo(() => [
    ...(canManage ? [{
      title: <b>{t("actions", "Actions")}</b>,
      key: "actions",
      width: 90,
      fixed: fixedStart(i18n.language),
      render: (_, r) => (
        <Button
          size="small"
          danger
          icon={<DeleteOutlined />}
          style={{ borderRadius: 7 }}
          onClick={() => removeMovement(r)}
        />
      ),
    }] : []),
    {
      title: <b>{t("docNo", "No.")}</b>,
      dataIndex: "document_no",
      width: 150,
      render: (v) => <span style={{ fontFamily: "monospace", fontSize: 12, fontWeight: 700 }}>{v}</span>,
    },
    {
      title: <b>{t("date", "Date")}</b>,
      dataIndex: "created_at",
      width: 150,
      render: (v) => <span style={{ color: T.muted, fontSize: 12 }}>{v ? dayjs(v).format("DD/MM/YYYY HH:mm") : "—"}</span>,
    },
    {
      title: <b>{t("type", "Type")}</b>,
      dataIndex: "type",
      width: 120,
      render: (v) => (
        <Tag color={v === "deposit" ? "green" : "red"} style={{ margin: 0, borderRadius: 6, fontWeight: 700 }}>
          {v === "deposit" ? t("cashDeposit", "Deposit") : t("cashWithdrawal", "Withdrawal")}
        </Tag>
      ),
    },
    {
      title: <b>{t("amount", "Amount")}</b>,
      dataIndex: "amount",
      align: "right",
      render: (v, r) => (
        <b style={{ color: r.type === "deposit" ? T.green : T.red }}>
          {r.type === "deposit" ? "+" : "−"}{formatCurrency(v, mainCode)}
        </b>
      ),
    },
    {
      title: <b>{t("reason", "Reason")}</b>,
      dataIndex: "reason",
      ellipsis: true,
      render: (v) => v || "—",
    },
    {
      title: <b>{t("user", "User")}</b>,
      dataIndex: "user",
      width: 130,
      render: (v) => <span style={{ color: T.sub, fontSize: 12 }}>{v || "—"}</span>,
    },
  ], [t, mainCode, canManage]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{ padding: "clamp(12px, 2.5vw, 24px)", background: T.bg, minHeight: "100%" }}>
      <PageHeader
        breadcrumb={[t("sidebarSectionReports", "Reports"), t("cashManagementTitle", "Cash Management")]}
        title={t("cashManagementTitle", "Cash Management")}
        subtitle={t("cashManagementHint", "Record manual cash deposits and withdrawals, and track the drawer balance")}
        action={(
          <Space wrap>
            <Button icon={<ReloadOutlined />} onClick={load} loading={loading} style={{ borderRadius: 9 }}>
              {t("currencyTotalsRefresh", "Refresh")}
            </Button>
            {canManage && (
              <>
                <Button
                  type="primary"
                  icon={<PlusCircleOutlined />}
                  style={{ borderRadius: 9, background: T.green, borderColor: T.green }}
                  onClick={() => openForm("deposit")}
                >
                  {t("cashDeposit", "Deposit")}
                </Button>
                <Button
                  danger
                  icon={<MinusCircleOutlined />}
                  style={{ borderRadius: 9 }}
                  onClick={() => openForm("withdrawal")}
                >
                  {t("cashWithdrawal", "Withdrawal")}
                </Button>
              </>
            )}
          </Space>
        )}
      />

      <div className="velan-stats-row">
        <StatCard
          icon={<WalletOutlined style={{ color: "#2563eb" }} />}
          label={t("cashOnHand", "Cash on Hand")}
          value={formatCurrency(cashOnHand, mainCode)}
          iconBg="#dbeafe"
        />
        <StatCard
          icon={<PlusCircleOutlined style={{ color: "#16a34a" }} />}
          label={t("cashTotalDeposits", "Total Deposits")}
          value={formatCurrency(stats.total_deposits, mainCode)}
          iconBg="#dcfce7"
          valueColor={T.green}
        />
        <StatCard
          icon={<MinusCircleOutlined style={{ color: "#e11d48" }} />}
          label={t("cashTotalWithdrawals", "Total Withdrawals")}
          value={formatCurrency(stats.total_withdrawals, mainCode)}
          iconBg="#fee2e2"
          valueColor={T.red}
        />
        <StatCard
          icon={<WalletOutlined style={{ color: "#7c3aed" }} />}
          label={t("cashNetMovements", "Net Movements")}
          value={formatCurrency(stats.net, mainCode)}
          iconBg="#ede9fe"
        />
      </div>

      <div style={{ ...cardStyle, padding: "16px 20px", marginBottom: 16 }}>
        <Space wrap size={[12, 12]} align="end">
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.muted, marginBottom: 4 }}>{t("dateRange", "Date range")}</div>
            <DateRangeFilter value={range} onChange={(v) => setRange(v || [null, null])} allowClear size="small" />
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.muted, marginBottom: 4 }}>{t("type", "Type")}</div>
            <Segmented
              value={typeFilter}
              onChange={setTypeFilter}
              options={[
                { label: t("all", "All"), value: "all" },
                { label: t("cashDeposit", "Deposit"), value: "deposit" },
                { label: t("cashWithdrawal", "Withdrawal"), value: "withdrawal" },
              ]}
            />
          </div>
        </Space>
      </div>

      <div style={{ ...cardStyle }}>
        <div style={{ padding: "14px 16px", borderBottom: `1px solid ${T.border}` }}>
          <span style={{ fontWeight: 700, fontSize: 15 }}>{t("cashHistoryTitle", "Cash Movement History")}</span>
        </div>
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={rows}
          pagination={{ ...pagination, showTotal: (total) => `${total}` }}
          scroll={{ x: 800 }}
        />
      </div>

      <Modal
        title={formType === "deposit" ? t("cashAddDeposit", "Add Cash Deposit") : t("cashAddWithdrawal", "Record Cash Withdrawal")}
        open={formOpen}
        onCancel={() => setFormOpen(false)}
        onOk={submitForm}
        okText={t("save", "Save")}
        confirmLoading={saving}
        destroyOnHidden
      >
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: T.muted, marginBottom: 6 }}>
            {t("amount", "Amount")} ({mainCode})
          </div>
          <InputNumber
            style={{ width: "100%" }}
            min={0}
            step="any"
            value={amount}
            onChange={setAmount}
            placeholder="0"
            autoFocus
          />
        </div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: T.muted, marginBottom: 6 }}>
            {t("reason", "Reason")}
          </div>
          <Input.TextArea
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t("optional", "Optional")}
          />
        </div>
      </Modal>
    </div>
  );
};

export default CashManagement;
